import React from "react";
import { styled, ToggleButton, ToggleButtonGroup } from "@mui/material";

import { colorGrayDark, colorGrayLight } from "../../styles/theme";
import ThumbsDownIcon from "../Icons/ThumbsDownIcon";
import ThumbsUpIcon from "../Icons/ThumbsUpIcon";

export enum RelevancyValue {
	IRRELEVANT,
	RELEVANT,
}

export type RelevancyVariant = "default" | "compact";

const variantConfig: Record<
	RelevancyVariant,
	{ group: object; button: object; iconSize: number }
> = {
	default: {
		group: { width: "100%", gap: "10px" },
		button: {
			borderRadius: "20px !important",
			height: "34px",
			width: "100%",
		},
		iconSize: 20,
	},
	compact: {
		group: { gap: "8px" },
		button: {
			borderRadius: "50% !important",
			height: "32px",
			width: "32px",
			minWidth: "32px",
			padding: 0,
		},
		iconSize: 16,
	},
};

const StyledToggleButton = styled(ToggleButton)({
	cursor: "pointer",
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	"&.MuiToggleButtonGroup-grouped:not(:first-of-type)": {
		borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
		marginLeft: 0,
	},
	"&.Mui-selected": {
		backgroundColor: colorGrayLight,
		outline: `1px solid ${colorGrayLight}`,
	},
	"&.Mui-selected:hover": {
		backgroundColor: colorGrayLight,
	},
});

interface Props {
	selectedValue: RelevancyValue | null;
	onChange: (value: RelevancyValue) => void;
	variant?: RelevancyVariant;
}

const RelevancyToggle: React.FC<Props> = ({
	selectedValue,
	onChange,
	variant = "default",
}) => {
	const config = variantConfig[variant];

	const handleSelect = (value: RelevancyValue) => {
		onChange(value);
	};

	return (
		<ToggleButtonGroup
			sx={config.group}
			value={selectedValue}
			exclusive
			onChange={(e, v) => {
				handleSelect(v);
			}}
			aria-label="relevance toggle"
		>
			<StyledToggleButton
				value={RelevancyValue.RELEVANT}
				aria-label="relevant"
				sx={config.button}
			>
				<ThumbsUpIcon sx={{ fontSize: config.iconSize, fill: colorGrayDark }} />
			</StyledToggleButton>
			<StyledToggleButton
				value={RelevancyValue.IRRELEVANT}
				aria-label="irrelevant"
				sx={config.button}
			>
				<ThumbsDownIcon
					sx={{ fontSize: config.iconSize, fill: colorGrayDark }}
				/>
			</StyledToggleButton>
		</ToggleButtonGroup>
	);
};

export default RelevancyToggle;
